import { fetchUserAttributes } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type { Plan } from "@cpos/shared/entitlements";
import type {
  MerchantRow,
  OnboardingStage,
  OverallStage,
  IdentityStage,
} from "./types";
import { MERCHANT_SELECTION_SET, SCAN_PAGE_LIMIT } from "./types";
import {
  stripePushOutcome,
  type BusinessProfilePatch,
  type FieldChange,
  type StripePushOutcome,
} from "./business_info_edit";
import type { PersonalInfoPatch } from "./personal_info_edit";

const client = generateClient<Schema>();

type UserProfile = Schema["UserProfile"]["type"];

/**
 * Effectively a boolean (0 = not seen, 1 = seen). Mobile gates on
 * `educationShown >= currentEducationVersion` (settings_service.ts), so this
 * must stay >= that constant for the override to keep working.
 */
export const TAP_TO_PAY_EDUCATION_SEEN = 1;

export function hasSeenTapToPayEducation(user: UserProfile): boolean {
  return (
    (user.settings?.tap_to_pay?.educationShown ?? 0) >=
    TAP_TO_PAY_EDUCATION_SEEN
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseJsonField(value: unknown): unknown {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

export function extractOnboardingStage(
  tracks: unknown,
): OnboardingStage | null {
  const parsed = parseJsonField(tracks);
  if (!parsed || typeof parsed !== "object") return null;
  const onboarding = (parsed as Record<string, unknown>)["onboarding"];
  if (!onboarding || typeof onboarding !== "object") return null;
  const stage = (onboarding as Record<string, unknown>)["stage"];
  return typeof stage === "string" ? (stage as OnboardingStage) : null;
}

function safeParseRecord(value: string): Record<string, number> {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function safeParseStringArray(value: string): string[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function mapToMerchantRow(raw: unknown): MerchantRow {
  const bp = raw as {
    id: string;
    businessName: string;
    plan?: string | null;
    subscriptionStatus?: string | null;
    lifecycle?: {
      overallStage?: string | null;
      identityStage?: string | null;
      tracks?: unknown;
      updatedAt?: number | null;
    } | null;
  };
  return {
    id: bp.id,
    businessName: bp.businessName,
    plan: (bp.plan as Plan | null) ?? null,
    overallStage: (bp.lifecycle?.overallStage as OverallStage | null) ?? null,
    onboardingStage: bp.lifecycle
      ? extractOnboardingStage(bp.lifecycle.tracks)
      : null,
    identityStage:
      (bp.lifecycle?.identityStage as IdentityStage | null) ?? null,
    subscriptionStatus: bp.subscriptionStatus ?? null,
    createdAt: null,
    proSince: null,
    updatedAt: bp.lifecycle?.updatedAt ?? null,
  };
}

// ── Repository ────────────────────────────────────────────────────────────────

/** One audit row for an info edit; `extra` is merged into the metadata beside `before` and `after`. */
export interface InfoChangeAuditEntry {
  action: "merchant.business_info_changed" | "merchant.personal_info_changed";
  businessId: string;
  targetLabel: string;
  changes: FieldChange<string>[];
  extra?: Record<string, unknown>;
}

export interface BrowseResult {
  items: MerchantRow[];
  nextToken: string | null;
}

export class MerchantsRepository {
  /** Direct lookup by BusinessProfile UUID. */
  async getById(id: string): Promise<MerchantRow | null> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- selectionSet dotted paths
    const opts: any = { selectionSet: [...MERCHANT_SELECTION_SET] };
    const { data, errors } = await client.models.BusinessProfile.get(
      { id },
      opts,
    );
    if (errors?.length) throw new Error(errors[0].message);
    return data ? mapToMerchantRow(data) : null;
  }

  /** Resolve a business via the indexed UserProfile.email GSI. */
  async findByEmail(email: string): Promise<MerchantRow | null> {
    const model = client.models.UserProfile as unknown as {
      listUserProfileByEmail: (
        key: { email: string },
        opts?: { limit?: number },
      ) => Promise<{
        data: { businessId?: string | null }[];
        errors?: { message: string }[];
      }>;
    };
    const { data, errors } = await model.listUserProfileByEmail(
      { email: email.trim().toLowerCase() },
      { limit: 5 },
    );
    if (errors?.length) throw new Error(errors[0].message);
    const businessId = data.find((u) => u.businessId)?.businessId;
    return businessId ? this.getById(businessId) : null;
  }

  /** Bounded scan with `businessName contains`. Cursor-paginated, capped by caller. */
  async searchByBusinessName(
    query: string,
    nextToken?: string,
  ): Promise<BrowseResult> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const opts: any = {
      limit: SCAN_PAGE_LIMIT,
      nextToken,
      selectionSet: [...MERCHANT_SELECTION_SET],
      filter: { businessName: { contains: query.trim() } },
    };
    const {
      data,
      nextToken: nt,
      errors,
    } = await client.models.BusinessProfile.list(opts);
    if (errors?.length) throw new Error(errors[0].message);
    return { items: (data ?? []).map(mapToMerchantRow), nextToken: nt ?? null };
  }

  /** Customer-name search via UserProfile firstName/lastName scan → resolve businesses. */
  async searchByCustomerName(
    query: string,
    nextToken?: string,
  ): Promise<BrowseResult> {
    const q = query.trim();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const opts: any = {
      limit: SCAN_PAGE_LIMIT,
      nextToken,
      selectionSet: ["businessId"],
      filter: {
        or: [{ firstName: { contains: q } }, { lastName: { contains: q } }],
      },
    };
    const {
      data,
      nextToken: nt,
      errors,
    } = await client.models.UserProfile.list(opts);
    if (errors?.length) throw new Error(errors[0].message);
    const ids = [
      ...new Set(
        (data ?? [])
          .map((u: { businessId?: string | null }) => u.businessId)
          .filter(Boolean) as string[],
      ),
    ];
    // Bound fan-out: a 500-row page could resolve up to 500 businesses; cap at 25 concurrent getById calls.
    const CONCURRENCY = 25;
    const rows: MerchantRow[] = [];
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const batch = ids.slice(i, i + CONCURRENCY);
      const settled = await Promise.allSettled(
        batch.map((id) => this.getById(id)),
      );
      for (const r of settled) {
        if (r.status === "fulfilled" && r.value) rows.push(r.value);
      }
    }
    return { items: rows, nextToken: nt ?? null };
  }

  /**
   * Cascade-updates the Cognito login email, UserProfile, all ProvisioningRequests,
   * and Stripe across systems for a given business.
   */
  async changeLoginEmail(
    businessProfileId: string,
    newEmail: string,
  ): Promise<{
    success?: boolean | null;
    cognitoUpdated?: boolean | null;
    userProfileUpdated?: boolean | null;
    provisioningUpdated?: number | null;
    stripeUpdated?: boolean | null;
    errors?: (string | null)[] | null;
  }> {
    const { data, errors } = await client.mutations.AdminProvision({
      action: "update",
      input: { changeLoginEmail: { businessProfileId, newEmail } },
    });
    if (errors?.length) throw new Error(errors[0].message);
    return data ?? { errors: ["No response"] };
  }

  /**
   * Reads the full, current BusinessProfile row. Used to build a patch from
   * live data when the loaded row may predate the update processor's write-back.
   */
  async getBusinessProfile(
    id: string,
  ): Promise<Schema["BusinessProfile"]["type"]> {
    const { data, errors } = await client.models.BusinessProfile.get({ id });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("Business profile not found");
    return data;
  }

  /**
   * Saves a support edit of the business details with the same write Hub makes.
   * `skipUpdateProcessor` must be false on every save: the processor's own
   * write-backs leave it true, and while it is true the Stripe push is
   * silently skipped.
   */
  async updateBusinessInfo(
    patch: BusinessProfilePatch,
  ): Promise<Schema["BusinessProfile"]["type"]> {
    return this.saveProfilePatch(patch);
  }

  /** Saves a support edit of the owner's contact details, under the same rule as `updateBusinessInfo`. */
  async updatePersonalInfo(
    patch: PersonalInfoPatch,
  ): Promise<Schema["BusinessProfile"]["type"]> {
    return this.saveProfilePatch(patch);
  }

  private async saveProfilePatch(
    patch: BusinessProfilePatch | PersonalInfoPatch,
  ): Promise<Schema["BusinessProfile"]["type"]> {
    const { data, errors } = await client.models.BusinessProfile.update({
      ...patch,
      skipUpdateProcessor: false,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("No response from BusinessProfile.update");
    return data;
  }

  /**
   * Records an info change on the merchant's audit trail. Resolves false
   * instead of throwing so the dialog can offer a retry.
   */
  async writeInfoChangeAudit(entry: InfoChangeAuditEntry): Promise<boolean> {
    try {
      const { sub, email } = await fetchUserAttributes();
      if (!sub || !email) return false;
      const { errors } = await client.models.AdminAuditLog.create({
        action: entry.action,
        actorSub: sub,
        actorEmail: email,
        targetType: "business_profile",
        targetId: entry.businessId,
        targetLabel: entry.targetLabel,
        metadata: JSON.stringify({
          before: Object.fromEntries(
            entry.changes.map((c) => [c.field, c.before]),
          ),
          after: Object.fromEntries(
            entry.changes.map((c) => [c.field, c.after]),
          ),
          ...entry.extra,
        }),
        occurredAt: new Date().toISOString(),
      });
      return !errors?.length;
    } catch {
      return false;
    }
  }

  /**
   * Polls the row until the update processor records a Stripe push for the
   * save made at `savedAt`. Polls rather than subscribes: `lastStripePush`
   * has field-level auth, which subscription payloads may redact.
   */
  async awaitStripePush(
    businessId: string,
    savedAt: string,
    timing: { waitMs: number; pollMs: number },
  ): Promise<StripePushOutcome> {
    const deadline = Date.now() + timing.waitMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, timing.pollMs));
      try {
        const { data } = await client.models.BusinessProfile.get({
          id: businessId,
        });
        const outcome = stripePushOutcome(data?.lastStripePush, savedAt);
        if (outcome) return outcome;
      } catch {
        // A failed poll is skipped; the deadline still bounds the wait.
      }
    }
    return { kind: "noAnswer" };
  }

  /**
   * Permanently delete a merchant — its BusinessProfile, every businessId-keyed
   * child record, and the Cognito users in `userIds`. Backed by the
   * CPOS-Developers-gated `purgeMerchant` mutation, which also refuses to run in
   * production. Returns the per-model deleted counts and any partial-failure
   * detail so callers can surface what did and didn't get cleaned up.
   */
  async purgeMerchant(businessId: string): Promise<{
    success: boolean;
    error?: string | null;
    deleted: Record<string, number>;
    cognitoDeleted: number;
    cognitoErrors: string[];
  }> {
    const { data, errors } = await client.mutations.purgeMerchant({
      businessId,
    });
    if (errors?.length) throw new Error(errors[0].message);
    if (!data) throw new Error("No response from purgeMerchant");
    return {
      success: data.success,
      error: data.error,
      deleted: safeParseRecord(data.deletedJson),
      cognitoDeleted: data.cognitoDeleted,
      cognitoErrors: safeParseStringArray(data.cognitoErrorsJson),
    };
  }

  /**
   * Sets whether the v1 Tap to Pay education guide counts as seen for a user
   * by writing `settings.tap_to_pay.educationShown` on their UserProfile. The
   * mobile app seeds its local "show the education screen" flag from this
   * value on the next profile load, so this unblocks merchants stuck on a
   * malformed education screen (they must restart the app to pick it up).
   *
   * DynamoDB replaces the whole `settings` map on update, so the profile is
   * re-fetched immediately before writing and its current settings merged
   * back in — merging from the admin page's stale snapshot could revert
   * settings the merchant changed in-app since the page loaded.
   */
  async setTapToPayEducationSeen(userId: string, seen: boolean): Promise<void> {
    const { data: fresh, errors: getErrors } =
      await client.models.UserProfile.get({ userId });
    if (getErrors?.length) throw new Error(getErrors[0].message);
    if (!fresh) throw new Error("User profile not found");

    const settings = {
      ...fresh.settings,
      tap_to_pay: {
        previouslyEnabled:
          fresh.settings?.tap_to_pay?.previouslyEnabled ?? false,
        educationShown: seen ? TAP_TO_PAY_EDUCATION_SEEN : 0,
      },
    };
    const { errors } = await client.models.UserProfile.update({
      userId,
      settings,
    });
    if (errors?.length) throw new Error(errors[0].message);
  }

  /** Unsorted browse page. */
  async browse(nextToken?: string): Promise<BrowseResult> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Amplify ModelFilter is too deeply complex to type-safely construct
    const opts: any = {
      limit: 50,
      nextToken,
      selectionSet: [...MERCHANT_SELECTION_SET],
    };
    const {
      data,
      nextToken: nt,
      errors,
    } = await client.models.BusinessProfile.list(opts);
    if (errors?.length) throw new Error(errors[0].message);
    return { items: (data ?? []).map(mapToMerchantRow), nextToken: nt ?? null };
  }
}
